import { useEffect, useRef } from 'react';
import type { SessionIdentity } from 'lib/auth/session-identity';
import { currentSessionIdentity, opaqueIdentity } from 'lib/auth/session-identity';
import { clearDedicatedOrderStorage } from 'lib/auth/token-store';
import { localStorageEvent } from 'lib/hooks/use-storage';
import { queryClient, syncAuthorizationHeader } from 'lib/tanstack-query-client';

/**
 * Keep this tab's cache honest about who is signed in.
 *
 * `@tokens` already reaches React through the storage hook, so a sign-out
 * elsewhere correctly flips the session state — but nothing drops the previous
 * customer's cached profile and orders. Left alone, a tab whose sibling signed
 * in as somebody else keeps serving the old profile as `isSuccess` while it
 * refetches, i.e. renders the wrong person's name.
 *
 * Identity is *derived* from the token rather than announced by each sign-in
 * path. Announcing drifts — two of the six token-writing paths had already
 * forgotten to clear the cache — and derivation also distinguishes the one case
 * that must NOT clear: a refresh, which rotates the access token for the very
 * same customer.
 */
export function useSessionIdentitySync() {
  const seen = useRef<SessionIdentity | undefined>(undefined);
  useEffect(() => {
    const evaluate = () => {
      const next = currentSessionIdentity();

      // Seed on first observation rather than compare, so StrictMode's double
      // effect can't read the mount as a change and clear the cache.
      if (seen.current === undefined) {
        seen.current = next;
        return;
      }
      if (next === seen.current) return;
      const previous = seen.current;
      seen.current = next;

      // An unreadable token tells us nothing, so it changes nothing.
      if (next === opaqueIdentity || previous === opaqueIdentity) return;

      // The header is a module singleton and is still pointing at the previous
      // customer. Correct it first, or the refetches below go out as them.
      syncAuthorizationHeader();

      // `clear()` does not abort in-flight requests, and the generated query
      // functions forward the abort signal — so cancel, or a response carrying
      // the old bearer can land in the freshly cleared cache.
      queryClient.cancelQueries();

      // Deferred: the storage hook dispatches its same-tab event from inside a
      // `setValue` updater, and clearing there would be a render-phase update.
      queueMicrotask(() => {
        if (previous === null) {
          // A guest tab whose sibling just signed in. Refetch in place rather
          // than wiping the event, hall and seat data they are looking at.
          queryClient.invalidateQueries();
          return;
        }
        queryClient.clear();
        // The previous customer's reservation or renewal, if this tab was
        // paying one. The sign-out that cleared it ran in the other tab only.
        clearDedicatedOrderStorage();
      });
    };

    // The native event fires only in *other* tabs; the custom one covers this
    // tab, including the token store's own writes.
    window.addEventListener('storage', evaluate);
    window.addEventListener(localStorageEvent, evaluate);
    evaluate();
    return () => {
      window.removeEventListener('storage', evaluate);
      window.removeEventListener(localStorageEvent, evaluate);
    };
  }, []);
}